import { InlineIcon } from "@iconify/react";
import { Form, Formik } from "formik";
import _ from "lodash";
import { DateTime } from "luxon";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import { GlobalModalsContext, NoScrollContext } from "../../context";
import jssVariables from "../../jss_setup";
import { modalGenericStyles } from "../../jss_setup/modals";
import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import { SearchButton } from "../common/Button";
import { DatepickerField } from "../forms/inputGroup/helpers";
import { validateHotelSearch } from "../MainSearchForm/hotel/helpers";
import { DestinationsPicker } from "../MainSearchForm/location";
import PaxPickerModalACC from "../MainSearchForm/modals/PaxPickerModalACC";
import { PaxPickerACC } from "../MainSearchForm/widgets/PaxPickerACC";
import { accSearchFormUpdate } from "../../actions/search/accommodation_search";
import { useHotelTypesSearch } from "../../hooks";

const styles = createUseStyles({
  ...modalGenericStyles,
  DeepHotelSearchForm: (props) => ({
    ...modalGenericStyles.modal,
    position: "fixed",
    height: "100vh",
    width: "100vw",
    zIndex: 9999,
    ..._.get(props, "customStyles.DeepHotelSearchForm", {}),
  }),
  searchModal: (props) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    background: jssVariables.colors.background.light,
    padding: jssVariables.normal_gap,
    borderRadius: jssVariables.border_radius,
    ..._.get(props, "customStyles.searchModal", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    justifyContent: "space-between",
    fontSize: "large",
    fontWeight: "bold",
    padding: [0, jssVariables.normal_gap],
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.header", {}),
  }),
  hotelSearchForm: (props) => ({
    display: "grid",
    gridTemplateRows: "repeat(4, max-content)",
    gridGap: jssVariables.normal_gap,
    padding: jssVariables.normal_gap,
    alignItems: "center",
    ..._.get(props, "customStyles.hotelSearchForm", {}),
  }),
  closeIcon: (props) => ({
    fontSize: "xx-large",
    cursor: "pointer",
    ..._.get(props, "customStyles.closeIcon", {}),
  }),
  searchBtnContr: (props) => ({
    width: "max-content",
    margin: "auto",
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    searchModal: (props) => ({
      width: "70rem",
      ..._.get(props, "customStyles.searchModal.smallscreen", {}),
    }),
    hotelSearchForm: (props) => ({
      gridTemplateRows: "unset",
      gridTemplateColumns: "repeat(3, 1fr) max-content",
      alignItems: "end",
      gridGap: jssVariables.half_gap,
      ..._.get(
        props,
        "customStyles.hotelSearchForm.smallscreen.smallscreen",
        {}
      ),
    }),
    searchBtnContr: (props) => ({
      margin: "unset",
      paddingBottom: "1px",
      ..._.get(props, "customStyles.searchBtnContr.smallscreen", {}),
    }),
  },
});
const DeepHotelSearchForm = () => {
  const [showPaxPickerModal, setShowPaxPickerModal] = useState(false);

  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { setShowHtlFormModal } = useContext(GlobalModalsContext);
  const { types } = useHotelTypesSearch();
  const dispatch = useDispatch();

  const customStyles = cStyler({
    type,
    brand,
    objName: "DeepHotelSearchForm",
  });

  const classes = styles({
    customStyles,
  });

  const { search_form, search_form_from_meta } = useSelector((state) => {
    return {
      search_form: state.HotelSearchForm,
      search_form_from_meta: state.HotelSearch.searchPayload,
    };
  });

  function createPayload({ form }) {
    const rooms = search_form.rooms;

    const payload = {
      rms: rooms,
      in: form.checkIn,
      out: form.checkOut,
      dst_c: form.destination_code,
      dst_m: form.destination_meta,
    };
    return payload;
  }

  async function onNewSearch(form) {
    const encoded_data = window.btoa(JSON.stringify(createPayload({ form })));
    window.open(`/deep-hotel-search/${encoded_data}`, "_blank");
    setShowHtlFormModal(false);
  }

  function onClickPax() {
    setShowPaxPickerModal(true);
  }

  const onFormUpdate = useCallback(
    (form) => dispatch(accSearchFormUpdate({ form })),
    [dispatch]
  );

  useEffect(() => {
    if (_.isEmpty(search_form_from_meta)) return;
    onFormUpdate(search_form_from_meta);
  }, [search_form_from_meta]);

  return (
    <div className={classes.DeepHotelSearchForm}>
      <div className={classes.searchModal}>
        <div className={classes.header}>
          <span>{t("performNewSearch")}</span>
          <InlineIcon
            id="closeDeepHotelSearchForm"
            icon="ei:close"
            className={classes.closeIcon}
            onClick={() => setShowHtlFormModal(false)}
          />
        </div>
        <Formik
          enableReinitialize={true}
          initialValues={{
            destinationObj: {
              destination_code: _.get(search_form, "destination_code", []),
              destination_meta: _.get(search_form, "destination_meta", {}),
            },
            datesArray: [search_form.check_in, search_form.check_out],
          }}
          onSubmit={async (values) => {
            if (validateHotelSearch(values)) {
              const [checkIn, checkOut] = values.datesArray;
              onNewSearch({
                ...search_form,
                checkIn,
                checkOut,
                destination_code: [...values.destinationObj.destination_code],
                destination_meta: {
                  ...values.destinationObj.destination_meta,
                },
              });
            }
          }}>
          <Form className={classes.hotelSearchForm}>
            <DestinationsPicker
              id="destinationsPickerDeepHotel"
              name="destinationObj"
              types={types}
              product_type={"ACC"}
              placeholder={t("destinationPlaceholder")}
              customStyles={{
                ..._.get(customStyles, "inputGroupField", {}),
                DestinationsPicker: _.get(
                  customStyles,
                  "DestinationsPicker",
                  {}
                ),
              }}
              autocomplete={true}
              value={_.get(search_form, "destination_code", [])}
            />
            <DatepickerField
              id="checkInOutDeepHotelDatepicker"
              label={t("checkInOut")}
              name="datesArray"
              selectsRange={true}
              dateFormat="MMM d, yyyy"
              minDate={DateTime.now().toISODate()}
              maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
              placeholder={"Mon DD, YYYY - Mon DD, YYYY"}
              withPortal={true}
              customStyles={_.get(customStyles, "inputGroupField", {})}
            />
            <PaxPickerACC
              id="deepHotelPaxPicker"
              customStyles={_.get(customStyles, "inputGroupField", {})}
              search_form={search_form}
              onClick={onClickPax}
            />
            <div className={classes.searchBtnContr}>
              <SearchButton
                id="deepHotelSearchBtn"
                type="submit"
                text={t("search")}
                loading={false}
                customStyles={_.get(customStyles, "searchBtn", {})}
              />
            </div>
          </Form>
        </Formik>
      </div>
      {showPaxPickerModal && (
        <PaxPickerModalACC onCancel={() => setShowPaxPickerModal(false)} />
      )}
    </div>
  );
};
DeepHotelSearchForm.propTypes = {};
export default DeepHotelSearchForm;
